import React, {useCallback, useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";

import {GeoMapProps} from "./types";
import {AppDispatch, RootState} from "../../../../store/store";
import {
    setCenter,
    setSelectedCountry,
    setSelectedCountryBelongsToOtherRegion,
    setSelectedState,
    setSelectedDistrict,
    setZoom,
} from "../../../../features/map/mapSlice";
import {LATAM_COUNTRY_NAMES, ROMANIA_REGION, USA_COUNTRY_NAMES} from "../../../../features/countries/types";
import {StateStatistics} from "../../../../features/statistics/types";
import {isRegion} from "../../../../config";
import renderGeographyForLATAM from "./LATAM/renderGeographyForLATAM";
import renderGeographyForRomania from "./Romania/renderGeographyForRomania";
import renderGeographyForUSA, {renderGeographyForUSADistrict} from "./USA/renderGeographyForUSA";
import {CenterProps} from "../../../../features/map/types";
import {getDistrictsForState} from "../../../../data/usaDistricts";

const getGeographyBounds = (geography: any): { center: CenterProps; zoom: number } => {
    let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
    const processRing = (ring: number[][]) => {
        ring.forEach(([lon, lat]) => {
            if (lon < minLon) minLon = lon;
            if (lon > maxLon) maxLon = lon;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        });
    };
    const geo = geography.geometry;
    if (geo?.type === "Polygon") {
        geo.coordinates.forEach(processRing);
    } else if (geo?.type === "MultiPolygon") {
        geo.coordinates.forEach((poly: number[][][]) => poly.forEach(processRing));
    }
    const center: CenterProps = [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
    const spanLon = maxLon - minLon;
    const spanLat = maxLat - minLat;
    const maxSpan = Math.max(spanLon, spanLat);
    // Scale zoom inversely to geographic span; 50/span gives good fill at scale 300
    const zoom = Math.max(5, Math.min(20, 50 / maxSpan));
    return { center, zoom };
};

export const GeoMap: React.FC<GeoMapProps> = ({geographies}) => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        map: {selectedCountry, caricomCountry, year, selectedState, selectedDistrict},
        countriesPerUser: {data: countries},
        statistics: {data: statisticsData},
    } = useSelector((state: RootState) => state);

    const usaCountry = countries.find(c => c.country_name === USA_COUNTRY_NAMES.USA);
    const statesWithData = usaCountry?.states.map(s => s.state_name) ?? [];

    const stateStatsMap = useMemo(() => {
        if (!isRegion.usa || !statisticsData) return undefined;
        const usaStats = statisticsData.find(s => s.states && s.states.length > 0);
        if (!usaStats?.states) return undefined;
        const map = new Map<string, StateStatistics>();
        for (const st of usaStats.states) {
            map.set(st.state_name, st);
        }
        return map;
    }, [statisticsData]);

    const handleCountrySelect = useCallback(
        (geography: any) => () => {
            // When viewing state counties, clicks do nothing
            if (selectedState) return;

            if (isRegion.usa) {
                const matchedState = usaCountry?.states.find(
                    s => s.state_name === geography.properties.name
                );
                if (matchedState) {
                    dispatch(setSelectedState(matchedState));
                    const districts = getDistrictsForState(matchedState.state_name);
                    if (districts.length === 1) {
                        dispatch(setSelectedDistrict(districts[0].district ?? "at-large"));
                    }
                }
                return;
            }

            const selectedCountry = countries.find(
                (country) => country.iso3 === geography.properties.iso3
            );
            const selectedCountryBelongsToCaricom = caricomCountry?.states.find(
                (state) => state.iso3 === geography.properties.iso3
            );

            if (selectedCountryBelongsToCaricom && caricomCountry) {
                dispatch(setCenter(caricomCountry.centro_id));
                dispatch(setSelectedCountry(caricomCountry));
                dispatch(setSelectedCountryBelongsToOtherRegion(selectedCountryBelongsToCaricom));
            }

            if (selectedCountry) {
                dispatch(setCenter(selectedCountry.centro_id));
                dispatch(setSelectedCountry(selectedCountry));
                dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
            }
        },
        [caricomCountry, countries, dispatch, selectedState, usaCountry]
    );

    const renderCaricom = useCallback(
        () =>
            geographies
                .filter((geo) =>
                    caricomCountry?.states.some(
                        (country) => country.iso3 === geo.properties.iso3
                    )
                )
                .map((geo) =>
                    renderGeographyForLATAM(geo, countries, handleCountrySelect, year, selectedCountry)
                ),
        [caricomCountry, countries, geographies, handleCountrySelect, selectedCountry]
    );

    const renderSelectedCountryInLATAM = useCallback(() => {
        if (isRegion.latam && selectedCountry?.country_name === LATAM_COUNTRY_NAMES.LATAM) {
            return geographies.map((geo) =>
                renderGeographyForLATAM(geo, countries, handleCountrySelect, year, selectedCountry)
            );
        } else {
            return geographies
                .filter((geo) => geo.properties.iso3 === selectedCountry?.iso3)
                .map((geo) =>
                    renderGeographyForLATAM(geo, countries, handleCountrySelect, year, selectedCountry)
                );
        }
    }, [countries, geographies, handleCountrySelect, selectedCountry]);

    const renderCountriesForLATAM = isRegion.latam
        ? (selectedCountry?.country_name === LATAM_COUNTRY_NAMES.CARICOM
            ? renderCaricom()
            : renderSelectedCountryInLATAM())
        : null;

    const renderRomania = useCallback(() => {
        if (isRegion.romania && selectedCountry?.country_name === ROMANIA_REGION.ROMANIA) {
            return geographies.map((geo) =>
                renderGeographyForRomania(geo, handleCountrySelect, selectedCountry)
            );
        }
    }, [geographies, handleCountrySelect, selectedCountry]);


    const handleDistrictClick = useCallback((districtId: string) => {
        dispatch(setSelectedDistrict(districtId));
    }, [dispatch]);

    const renderUSA = useCallback(() => {
        if (!isRegion.usa) return null;

        if (selectedState) {
            // District-level view
            return geographies
                .filter((geo) => geo.geometry?.type !== "Point")
                .map((geo) => renderGeographyForUSADistrict(geo, selectedDistrict, handleDistrictClick));
        }

        // National view: render states, clickable
        return geographies.map((geo) =>
            renderGeographyForUSA(geo, handleCountrySelect, selectedCountry, selectedState, statesWithData, stateStatsMap)
        );
    }, [geographies, handleCountrySelect, handleDistrictClick, selectedCountry, selectedState, selectedDistrict, stateStatsMap]);

    const renderAllLATAM = useCallback(() => {
        geographies.map((geo) =>
            renderGeographyForLATAM(geo, countries, handleCountrySelect, year, selectedCountry)
        );
    }, [caricomCountry, countries, geographies, handleCountrySelect, selectedCountry]);

    return (
        // @ts-ignore
        <>
            {isRegion.romania
                ? renderRomania()
                : isRegion.usa
                    ? renderUSA()
                    : isRegion.latam && selectedCountry
                        ? renderCountriesForLATAM
                        : renderAllLATAM()}
        </>
    );
};
